import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Brain, 
  Zap, 
  Cloud, 
  FileText, 
  MessageSquare, 
  Smartphone,
  Shield,
  Server,
  TrendingUp,
  Users,
  Search,
  BarChart3,
  Network,
  Lock,
  Cpu,
  Database
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import AIAgentNetwork from './AIAgentNetwork';
const securityImage = '/lovable-uploads/e1326663-b18a-4737-8e4a-cf424ae6d543.png';

const TechServicesOverview = () => {
  const { t } = useLanguage();
  
  const coreServices = [
    {
      icon: Brain,
      title: t('tech.agents.title'),
      description: t('tech.agents.desc'),
      component: AIAgentNetwork,
      color: 'text-primary',
      glow: 'shadow-glow-primary'
    },
    {
      icon: Shield,
      title: t('tech.security.title'),
      description: t('tech.security.desc'),
      image: securityImage,
      color: 'text-accent',
      glow: 'shadow-glow-accent'
    }
  ];

  const advancedServices = [
    {
      icon: Network,
      title: t('tech.network.title'),
      description: t('tech.network.desc'),
      color: 'text-primary'
    },
    {
      icon: Database,
      title: t('tech.data.title'),
      description: t('tech.data.desc'),
      color: 'text-accent'
    },
    {
      icon: Lock,
      title: t('tech.zerotrust.title'),
      description: t('tech.zerotrust.desc'),
      color: 'text-primary'
    },
    {
      icon: Cpu,
      title: t('tech.edge.title'),
      description: t('tech.edge.desc'),
      color: 'text-accent'
    },
    {
      icon: TrendingUp,
      title: t('tech.analytics.title'),
      description: t('tech.analytics.desc'),
      color: 'text-primary'
    },
    {
      icon: Zap,
      title: t('tech.orchestration.title'),
      description: t('tech.orchestration.desc'),
      color: 'text-accent'
    }
  ];

  return (
    <section className="py-24 bg-gradient-dark">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('tech.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
            {t('tech.description')}
          </p>
        </div>

        {/* Core Services */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {coreServices.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card key={index} className={`group hover:${service.glow} transition-tech overflow-hidden bg-gradient-surface border-primary/20`}>
                {service.component && (
                  <div className="aspect-video relative overflow-hidden">
                    <service.component />
                  </div>
                )}
                {service.image && (
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-tech duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="p-3 bg-card/50 backdrop-blur-md rounded-lg border border-primary/30">
                        <IconComponent className={`h-8 w-8 ${service.color}`} />
                      </div>
                    </div>
                  </div>
                )}
                <CardHeader className="pb-6">
                  <CardTitle className="flex items-center space-x-3 text-2xl">
                    {!service.image && !service.component && (
                      <div className={`p-3 bg-card/50 rounded-lg border border-primary/30`}>
                        <IconComponent className={`h-6 w-6 ${service.color}`} />
                      </div>
                    )}
                    <span>{service.title}</span>
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* Advanced Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {advancedServices.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card key={index} className="group hover:shadow-tech transition-tech bg-gradient-surface border-primary/20">
                <CardHeader className="pb-6">
                  <CardTitle className="flex items-center space-x-3 text-xl">
                    <div className={`p-3 rounded-lg bg-card/30 border border-primary/20 group-hover:${service.color === 'text-primary' ? 'shadow-glow-primary' : 'shadow-glow-accent'} transition-tech`}>
                      <IconComponent className={`h-6 w-6 ${service.color}`} />
                    </div>
                    <span>{service.title}</span>
                  </CardTitle>
                  <CardDescription className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* Infrastructure Metrics */}
        <div className="bg-gradient-surface rounded-2xl border border-primary/20 p-8 mb-16 shadow-tech">
          <h3 className="text-2xl font-bold text-center mb-8 bg-gradient-tech bg-clip-text text-transparent">
            {t('tech.performance.title')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-tech bg-clip-text text-transparent mb-2">99.99%</div>
              <div className="text-sm text-muted-foreground">{t('tech.performance.uptime')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-tech bg-clip-text text-transparent mb-2">&lt;10ms</div>
              <div className="text-sm text-muted-foreground">{t('tech.performance.response')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-tech bg-clip-text text-transparent mb-2">500M+</div>
              <div className="text-sm text-muted-foreground">{t('tech.performance.operations')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-tech bg-clip-text text-transparent mb-2">100%</div>
              <div className="text-sm text-muted-foreground">{t('tech.performance.automated')}</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/services">
            <Button variant="hero" size="lg" className="text-lg">
              {t('tech.cta')}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TechServicesOverview;